import type { ReactElement, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useEnabledModules } from "../features/modules/useEnabledModules";
import { useNotificationStream } from "../features/notifications/useNotificationStream";
import { useAuthStore } from "./store";

// Guard для роутов, требующих авторизации.
//
// Если identity в auth-store пуст — `<Navigate to="/login" replace />`.
// После #916 JWT в JS нет (он в HttpOnly cookie); признак «залогинен»
// — наличие identity-флага в store. `replace` — чтобы повторный
// «назад» не возвращал на закрытую страницу. `state={ from }` — после
// успешного login страница логина может вернуть user'а туда, откуда
// его отправили (см. LoginPage useNavigate с state).
//
// SSE-стрим live-нотификаций подключается через отдельный
// компонент `<NotificationStreamSubscriber />`, который рендерится
// ВНУТРИ branch'а «identity есть». Раньше hook вызывался
// unconditionally в `ProtectedRoute`, до early-return на
// `identity === null` — это корректно работало (внутри был guard),
// но запутывало: получалось «hook на странице без auth», что нарушает
// «single responsibility» auth-компонента. Вынос в отдельный
// subscriber делает поток зависимостей очевидным: компонент ⇒
// identity есть ⇒ hook → SSE.
export interface ProtectedRouteProps {
  readonly children: ReactNode;
}

/**
 * Невидимый компонент-«хост» для useNotificationStream.
 *
 * Возвращает null, потому что hook сам по себе UI не рисует —
 * notifications.show(...) из Mantine приклеивает toast'ы к
 * `<Notifications/>` provider'у на root-уровне.
 *
 * `trackStatus: true` — этот подписчик единственный владелец
 * глобального индикатора статуса стрима (default хука — `false`,
 * fail-safe: владение объявляется явно, а не наследуется молча).
 *
 * Подписку обслуживает модуль `notifications`: пока его нет в наборе
 * (или набор не загружен), поток не открывается (ADR-0083, п. 3–4).
 * Попутно этот вызов стартует загрузку состава для всей защищённой
 * ветки.
 */
function NotificationStreamSubscriber(): null {
  const modules = useEnabledModules();
  useNotificationStream({
    trackStatus: true,
    enabled: modules.isEnabled("notifications"),
  });
  return null;
}

export function ProtectedRoute({
  children,
}: ProtectedRouteProps): ReactElement {
  const identity = useAuthStore((state) => state.identity);
  const location = useLocation();

  if (identity === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Identity есть → монтируем subscriber (hook исполнится только тут)
  // и render'им children. Fragment-обёртка даёт нулевую DOM-надстройку.
  return (
    <>
      <NotificationStreamSubscriber />
      {children}
    </>
  );
}
